import type {
  Claim,
  DashboardSummary,
  Device,
  DeviceView,
  DeviceListItem,
  Merchant,
  ProcessingJob,
  Protection,
  ProtectionView,
  Receipt,
  ReceiptDetail,
  ReceiptListItem,
  Reminder,
  StoredDocument,
} from '@shared/types'
import { getDeviceId, getToken, setToken } from './session'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const OFFLINE_MESSAGE = 'Nema internetske veze. Prikazujemo posljednje učitane podatke.'

let sessionPromise: Promise<string> | null = null

async function ensureToken(): Promise<string> {
  const existing = await getToken()
  if (existing) return existing
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const deviceId = await getDeviceId()
      const response = await fetch('/api/session', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId }),
      })
      if (!response.ok) throw new ApiError(response.status, 'session_failed', 'Nije moguće pokrenuti sesiju.')
      const data = (await response.json()) as { token: string }
      await setToken(data.token)
      return data.token
    })().finally(() => {
      sessionPromise = null
    })
  }
  return sessionPromise
}

export async function ensureSession(): Promise<void> {
  await ensureToken()
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await ensureToken()
  let response: Response
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        ...(init.headers as Record<string, string> | undefined),
        authorization: `Bearer ${token}`,
      },
    })
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE)
  }

  if (response.status === 204) return undefined as T
  const isJson = (response.headers.get('content-type') ?? '').includes('application/json')
  const payload = isJson ? await response.json() : null

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: unknown } } | null)?.error
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? 'Došlo je do greške.',
      error?.details,
    )
  }
  return payload as T
}

async function requestBlob(path: string): Promise<Blob> {
  const token = await ensureToken()
  let response: Response
  try {
    response = await fetch(path, { headers: { authorization: `Bearer ${token}` } })
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE)
  }
  if (!response.ok) {
    const isJson = (response.headers.get('content-type') ?? '').includes('application/json')
    const payload = isJson
      ? await response.json() as { error?: { code: string; message: string; details?: unknown } }
      : null
    throw new ApiError(
      response.status,
      payload?.error?.code ?? 'image_unavailable',
      payload?.error?.message ?? 'Fotografija trenutačno nije dostupna.',
      payload?.error?.details,
    )
  }
  return response.blob()
}

function json<T>(path: string, method: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export interface DeviceDetailResponse extends DeviceListItem {
  receipt: Receipt | null
  documents: StoredDocument[]
  claims: Claim[]
  contact: {
    merchant: Merchant | null
    service_url: string | null
    service_provider: string | null
    explanation: string
  }
}

export interface ProtectionInput {
  id?: string
  type: Protection['type']
  provider_name?: string | null
  responsible_party?: string | null
  jurisdiction?: string | null
  starts_at?: string | null
  expires_at?: string | null
  duration_months?: number | null
  evidence_status: Protection['evidence_status']
  evidence_source?: string | null
  registration_required?: boolean
  registration_deadline?: string | null
  registration_confirmed?: boolean
  terms_summary?: string | null
  service_url?: string | null
}

export interface ConfirmItemInput {
  item_id: string
  track: boolean
  name: string
  brand?: string | null
  model?: string | null
  serial_number?: string | null
  location?: string | null
  category: Device['category']
  protections: ProtectionInput[]
}

export const api = {
  ensureSession,
  dashboard: () => request<DashboardSummary>('/api/dashboard'),

  devices: (includeArchived = false) =>
    request<{ devices: DeviceListItem[] }>(`/api/devices${includeArchived ? '?include_archived=1' : ''}`),
  device: (id: string) => request<DeviceDetailResponse>(`/api/devices/${id}`),
  updateDevice: (id: string, body: Record<string, unknown>) =>
    json<{ device: DeviceView; protections: ProtectionView[] }>(`/api/devices/${id}`, 'PATCH', body),
  deleteDevice: (id: string) => request<{ ok: true }>(`/api/devices/${id}`, { method: 'DELETE' }),
  devicePhoto: (id: string) => requestBlob(`/api/devices/${id}/photo`),
  uploadDevicePhoto: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<{ device: DeviceView }>(`/api/devices/${id}/photo`, { method: 'PUT', body: form })
  },
  deleteDevicePhoto: (id: string) =>
    request<{ device: DeviceView }>(`/api/devices/${id}/photo`, { method: 'DELETE' }),

  receipts: () => request<{ receipts: ReceiptListItem[] }>('/api/receipts'),
  receipt: (id: string) => request<ReceiptDetail>(`/api/receipts/${id}`),
  reanalyze: (id: string) => json<{ job_id: string }>(`/api/receipts/${id}/analyze`, 'POST', {}),
  confirmReceipt: (id: string, body: { receipt?: Record<string, unknown>; items: ConfirmItemInput[] }) =>
    json<{
      created_device_ids: string[]
      device_mappings: Array<{ item_id: string; device_id: string }>
    }>(`/api/receipts/${id}/confirm`, 'POST', body),

  uploadReceipt: async (files: File[], sourceType: 'camera' | 'photo' | 'pdf') => {
    const form = new FormData()
    for (const file of files) form.append('files', file)
    form.append('source_type', sourceType)
    return request<{ receipt: Receipt; job_id: string }>('/api/receipts', { method: 'POST', body: form })
  },

  uploadDocument: async (file: File, type: string, extra: Record<string, string> = {}) => {
    const form = new FormData()
    form.append('file', file)
    form.append('type', type)
    for (const [key, value] of Object.entries(extra)) form.append(key, value)
    return request<{ document: StoredDocument }>('/api/documents', { method: 'POST', body: form })
  },

  job: (id: string) => request<{ job: ProcessingJob }>(`/api/jobs/${id}`),

  claims: () => request<{ claims: Array<{ claim: Claim; device: DeviceView | null }> }>('/api/claims'),
  claim: (id: string) =>
    request<{
      claim: Claim
      device: DeviceView | null
      contact: DeviceDetailResponse['contact'] | null
      documents: StoredDocument[]
    }>(`/api/claims/${id}`),
  createClaim: (body: Record<string, unknown>) => json<{ claim: Claim }>('/api/claims', 'POST', body),
  patchClaim: (id: string, body: Record<string, unknown>) => json<{ claim: Claim }>(`/api/claims/${id}`, 'PATCH', body),
  generateLetter: (id: string) => json<{ letter: string }>(`/api/claims/${id}/generate-letter`, 'POST', {}),

  reminders: () => request<{ reminders: Reminder[] }>('/api/reminders'),
  patchReminder: (id: string, status: Reminder['status']) =>
    json<{ reminder: Reminder }>(`/api/reminders/${id}`, 'PATCH', { status }),

  seedDemo: () => json<{ seeded: boolean }>('/api/demo/seed', 'POST', {}),
  purge: () => request<{ documents: number }>('/api/account/data', { method: 'DELETE' }),
}

export async function downloadAccountExport(): Promise<void> {
  const token = await ensureToken()
  const response = await fetch('/api/account/export', {
    headers: { authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new ApiError(response.status, 'export_failed', 'Izvoz podataka nije uspio.')
  const disposition = response.headers.get('content-disposition') ?? ''
  const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? 'racunko-export.json'
  const url = URL.createObjectURL(await response.blob())
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

/** Dokumenti se poslužuju samo autoriziranom rutom — nikad javnim URL-om. */
export async function fetchDocumentBlobUrl(documentId: string): Promise<string> {
  const token = await ensureToken()
  const response = await fetch(`/api/documents/${documentId}`, {
    headers: { authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new ApiError(response.status, 'document_unavailable', 'Dokumentu nije moguće pristupiti.')
  return URL.createObjectURL(await response.blob())
}
